import React, { useEffect, useState } from "react";
import Head from "next/head";
import { FaFileUpload } from "react-icons/fa";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import { AiOutlineCheck } from "react-icons/ai";
import { FaDownload } from "react-icons/fa";
import JSZip from "jszip";
import styles from "../../signature.module.css";
import Blog from "../../../../components/CalculatorBlogs/Blog";
import SignaturePopup from "../../../../components/SignaturePopup";
import PdfToolsLinks from "../../../../components/PdfToolsLinks";

GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

const BlogData = [
  {
    id: 1,
    question: "What is PDF to PNG Tool?",
    answer: (
      <div className="">
        <p className="">
          A PDF to PNG tool is a utility that enables you to convert PDF
          documents into PNG image files. It allows you to extract images from
          the PDF or save each page as a separate PNG image.
        </p>
      </div>
    ),
  },
  {
    id: 2,
    question: "How to Convert PDFs to PNGs Online?",
    answer: (
      <div className="">
        <p className="">
          To convert PDFs to PNGs online, you need to upload the PDFs you want
          to convert. After uploading, the tool processes the PDF and generates
          PNG images for each page. You can then download the converted PNG
          files.
        </p>
      </div>
    ),
  },
  {
    id: 3,
    question: "How to Use the PDF to PNG Conversion Tool?",
    answer: (
      <div className="">
        <p className="">
          Using the PDF to PNG conversion tool is easy. Upload your PDF file,
          and the tool will automatically extract images and convert each page
          to PNG format. You can then download the PNG images individually or as
          a single ZIP file.
        </p>
      </div>
    ),
  },
  {
    id: 4,
    question: "Are There Any Limitations on PDF Size or Number of Pages?",
    answer: (
      <div className="">
        <p className="">
          The tool typically supports PDFs of various sizes and page counts.
          However, very large PDFs might take longer to process. There are
          usually no strict limitations on the number of pages, but {`it's`}{" "}
          recommended to check the specific {`tool's`} guidelines for any
          restrictions.
        </p>
      </div>
    ),
  },
  {
    id: 5,
    question: "Is the PDF to PNG Conversion Process Secure?",
    answer: (
      <div className="">
        <p className="">
          Yes, reputable online PDF to PNG conversion tools prioritize user
          privacy and security. They use encryption protocols to ensure the
          safety of your files. However, {`it's`} advisable to use trusted and
          well-known platforms to convert your sensitive documents.
        </p>
      </div>
    ),
  },
];

const PdfToPng = () => {
  const [pdf, setPdf] = useState(null);
  const [images, setImages] = useState([]);
  const [pageRendering, setPageRendering] = useState(false);
  const [loader, setLoader] = useState(false);
  const [pagesToDownload, setPagesToDownload] = useState([]);
  const [signaturePopup, setSignaturePopup] = useState(false);

  const scrollToPreview = () => {
    const element = document.getElementById("preview-pdfs");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    showPdf(file);
    scrollToPreview();
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    showPdf(file);
    if (file) {
      scrollToPreview();
    }
  };

  const showPdf = async (file) => {
    try {
      const uri = URL.createObjectURL(file);
      const _PDF_DOC = await getDocument({ url: uri }).promise;
      setPdf(_PDF_DOC);
      setPagesToDownload(
        Array.from({ length: _PDF_DOC.numPages }, (_, i) => i + 1),
      );
    } catch (error) {
      alert(error.message);
    }
  };

  const renderPage = async () => {
    setPageRendering(true);
    const imagesList = [];

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1 });
      const canvas = document.createElement("canvas");
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const renderContext = canvas.getContext("2d");
      await page.render({ canvasContext: renderContext, viewport: viewport })
        .promise;
      const img = canvas.toDataURL("image/png");
      imagesList.push(img);
    }

    setImages(imagesList);
    setPageRendering(false);
  };

  const downloadImages = () => {
    if (!images.length > 0) return;
    setLoader(true);
    const fileName = "pdf-to-png.zip";
    const zip = new JSZip();
    const imgFolder = zip.folder("images");
    for (let i = 0; i < images.length; i++) {
      if (!pagesToDownload.includes(i + 1)) continue;
      const base64 = images[i].replace(/^data:image\/(png|jpg);base64,/, "");
      imgFolder.file(`image-${i + 1}.png`, base64, { base64: true });
    }
    zip.generateAsync({ type: "blob" }).then((content) => {
      const url = window.URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    });
    setLoader(false);
    setTimeout(() => {
      setSignaturePopup(true);
    }, 2000);
  };

  useEffect(() => {
    pdf && renderPage();
  }, [pdf]);

  const addOrRemovePage = (page) => {
    if (pagesToDownload.includes(page)) {
      setPagesToDownload(pagesToDownload.filter((p) => p !== page));
    } else {
      setPagesToDownload([...pagesToDownload, page]);
    }
  };

  return (
    <>
      <div className="w-full mt-20 sm:mt-24">
        <Head>
          <meta charSet="utf-8" />
          <title>PDF to PNG Tool | BoloForms</title>
          <meta
            name="description"
            content="Convert PDF files to PNG images online for free. No software installation required."
          />
          <meta
            name="keywords"
            content="pdf to image, pdf to images, pdf to photo, pdf to image boloforms, pdf to png boloforms, pdf to png, pdf to png converter, convert pdf to png, pdf to png online, pdf to png free, pdf to png tool, pdf to png conversion, pdf to png converter online, pdf to png converter free, pdf to png converter tool"
          />
          <meta name="author" content="BoloForms" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0"
          />
          <link
            rel="canonical"
            href="https://www.bolosign.com/signature/pdf-tools/pdf-to-png"
          />
          <meta property="og:title" content="PDF to PNG Tool | BoloForms" />
          <meta
            property="og:description"
            content="Convert PDF files to PNG images online for free. No software installation required."
          />
          <meta
            property="og:url"
            content="https://www.bolosign.com/signature/pdf-tools/pdf-to-png"
          />
          <meta property="og:site_name" content="BoloForms" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content="BoloForms" />
          <meta property="og:type" content="website" />
          <meta property="og:locale" content="en_US" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@BoloForms" />
          <meta name="twitter:creator" content="@BoloForms" />
          <meta name="twitter:title" content="PDF to PNG Tool | BoloForms" />
          <meta
            name="twitter:description"
            content="Convert PDF files to PNG images online for free. No software installation required."
          />
          <link
            rel="canonical"
            href="https://www.bolosign.com/signature/pdf-tools/pdf-to-png/"
          />
        </Head>
        <div className="">
          <div className="max-w-[1280px] mx-auto px-2 md:px-10 py-20 rounded-xl relative flex flex-col gap-20">
            <div className="text-violet-500 text-center">
              <h1 className="text-3xl md:text-6xl text-gray-800">
                PDF to PNG Tool
              </h1>
              <p className="text-lg md:text-2xl mt-4 text-gray-600">
                Convert PDF files to PNG images online for free.
              </p>
            </div>
            <div
              className="extraOutline p-4 bg-white bg-whtie mx-auto rounded-lg w-full md:w-[600px]"
              onDragEnter={(e) => e.preventDefault()}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <div className="file_upload px-2 md:px-20 py-5 md:py-14 relative border-4 border-dotted border-gray-300 rounded-lg">
                <FaFileUpload className="text-gray-400 w-20 md:w-32 mx-auto mb-6 md:mb-10 text-[80px] md:text-[120px]" />
                <div className="input_field flex flex-col w-full mx-auto text-center">
                  <label>
                    <input
                      onChange={handlePdfUpload}
                      className="text-sm cursor-pointer w-36 hidden"
                      type="file"
                      accept=".pdf"
                    />
                    <div className="text bg-violet-600 text-white text-xl border border-gray-300 rounded-xl font-semibold cursor-pointer py-3 md:py-4 hover:bg-indigo-500">
                      Upload PDF File
                    </div>
                  </label>

                  <div className="title text-gray-500 uppercase text-lg md:text-xl">
                    or drop file here
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            id="preview-pdfs"
            className="max-w-[1280px] mx-auto flex flex-col gap-14 justify-center items-center"
          >
            {pdf && (
              <>
                <h1 className="text-3xl font-light text-center text-gray-700">
                  Disselect Pages that you {`don't`} want
                </h1>

                <div className="relative w-full h-[400px] flex flex-wrap justify-center gap-4 border-2 md:px-10 py-4 md:py-14 md:rounded-xl overflow-y-scroll scrollbar-thin scrollbar-thumb-violet-500 scrollbar-rounded-[12px]">
                  {images.map((image, index) => (
                    <div
                      key={index}
                      className="border drop-shadow-2xl w-fit relative flex justify-center items-center flex-wrap"
                    >
                      <div className="border-1 drop-shadow-lg p-4 bg-white w-[230px]">
                        <img
                          id="image-generated"
                          src={image}
                          alt="pdfImage"
                          className="w-[full"
                        />
                      </div>
                      <div className="absolute top-2 left-2 w-8 h-8 bg-violet-500 rounded-full text-xl text-white flex justify-center items-center cursor-pointer">
                        {index + 1}
                      </div>
                      <div
                        onClick={() => addOrRemovePage(index + 1)}
                        className={`absolute top-2 right-2 w-8 h-8 ${
                          pagesToDownload.includes(index + 1)
                            ? "bg-violet-500"
                            : "bg-gray-400"
                        } rounded-full text-xl text-white flex justify-center items-center cursor-pointer`}
                      >
                        <AiOutlineCheck />
                      </div>
                    </div>
                  ))}
                  <div
                    id="page-loader"
                    className={`${
                      pageRendering
                        ? "absolute w-full top-0 left-0 h-full flex justify-center items-center bg-white z-50"
                        : "hidden"
                    }`}
                  >
                    <svg
                      aria-hidden="true"
                      class="w-14 h-14 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-violet-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  </div>
                </div>
              </>
            )}
            {images.length > 0 && (
              <div id="split-pdfs" className="w-full">
                <button
                  onClick={downloadImages}
                  className="bg-violet-600 mx-auto w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400"
                >
                  {loader ? (
                    <div
                      class={`flex items-center text-white space-x-2 ${styles.loader}`}
                    >
                      <div class="">Downloading</div>
                      <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                      <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                      <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                    </div>
                  ) : (
                    <>
                      Download Zip <FaDownload className="text-3xl" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {signaturePopup && (
            <SignaturePopup
              onPopupClose={() => setSignaturePopup(false)}
              source="PDF_TO_PNG"
            />
          )}

          <div className="mt-20">
            <div className="mb-20">
              <PdfToolsLinks />
            </div>
            <Blog heading="PDF To PNG Tool FAQs" BlogData={BlogData} />
          </div>
        </div>
      </div>
    </>
  );
};

export default PdfToPng;
